import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from '../../../base/services/toast.service';
import { SpinnerService } from '../../../base/spinner/spinner.service';
import {
  ProductTemplateClient,
  SellerProfileClient,
  ProductTemplateDTO,
  CreateProductTemplateDTO,
  UpdateProductTemplateDTO,
  ProductConflictDTO,
  PublishProductFromTemplateDTO,
  ProductDTO,
  SellerProfileDTO,
} from '../../../base/models/models.swagger.generated';
import { ErrorType } from '../../../core/routing/error-types';
import { IPagedItems } from 'src/app/base/shared/table/paged-items.interface';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable()
export class ProductTemplateService {
  constructor(
    private productTemplateClient: ProductTemplateClient,
    private sellerProfileClient: SellerProfileClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private http: HttpClient
  ) { }

  public getData(pagedQuery: PagedQueryModel): Observable<IPagedItems> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient
      .searchTemplates(
        pagedQuery.filters,
        pagedQuery.skip,
        pagedQuery.take,
        pagedQuery.sortBy,
        pagedQuery.sortDirection
      )
      .pipe(
        tap(() => {
          this.spinnerService.showSpinner = false;
        }),
        catchError((error: any) => {
          this.spinnerService.showSpinner = false;
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError(
              'i18n.errors.server-error.title',
              'i18n.errors.server-error.message',
              null,
              ErrorType.serverError
            );
          }
          return throwError(() => error);
        })
      );
  }

  public getRecord(id: number): Observable<ProductTemplateDTO> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient.getTemplate(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public addRecord(data: CreateProductTemplateDTO): Observable<ProductTemplateDTO> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient.createTemplate(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.product-template.messages.add-title',
          'i18n.domain.product-template.messages.add-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public updateRecord(data: UpdateProductTemplateDTO): Observable<ProductTemplateDTO> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient.updateTemplate(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.product-template.messages.update-title',
          'i18n.domain.product-template.messages.update-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public deleteRecord(id: number): Observable<void> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient.deleteTemplate(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.product-template.messages.delete-title',
          'i18n.domain.product-template.messages.delete-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public uploadImage(file: File, tempGuid: string): Observable<string> {
    this.spinnerService.showSpinner = true;
    const formData = new FormData();
    formData.append('file', file);
    const url = `${environment.apiServerUrl}/api/v1/ProductTemplate/UploadTemplateImage?tempGuid=${encodeURIComponent(tempGuid)}`;
    return this.http.post(url, formData, { responseType: 'text' }).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public checkConflicts(templateIds: number[]): Observable<ProductConflictDTO[]> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient.checkPublishConflicts(templateIds).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public publishTemplates(requests: PublishProductFromTemplateDTO[]): Observable<ProductDTO[]> {
    this.spinnerService.showSpinner = true;
    return this.productTemplateClient.publishFromTemplates(requests).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.product-template.messages.publish-title',
          'i18n.domain.product-template.messages.publish-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public getSellerProfiles(): Observable<SellerProfileDTO[]> {
    this.spinnerService.showSpinner = true;
    return this.sellerProfileClient.getMySellerProfiles().pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }
}
